import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  ClipboardList,
  Gem,
  Mail,
  Megaphone,
  MessagesSquare,
  Phone,
  SquarePen,
  StickyNote,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import ACL_RELATIONSHIPS from "acl-relationships";
import { queryClient } from "App";
import CallsTable from "components/CallsTableV3/CallsTableV3";
import { linkedTabSections, useLinkedTables } from "components/linkedTables";
import CallModal from "components/modals/CallModal";
import ConfirmDialog from "components/modals/ConfirmDialog";
import PageContainer, { SECTION_TABLE_STYLES } from "components/PageContainer";
import RevenueOpportunityTable from "components/RevenueOpportunitiesTableV3/RevenueOpportunitiesTableV3";
import { DetailBreadcrumb } from "components/sidebar/DetailBreadcrumb";
import SidebarDetailLayout from "components/SidebarDetailLayout";
import AuditLog from "components/views/AuditLog";
import NotesView from "components/views/NotesView";
import ResponsesView from "components/views/ResponsesView";
import { noTag } from "constants/Tag";
import { isContactCallable, isContactEmailable } from "helpers/contacts";
import { getSession, removeSession } from "helpers/sessionHelpers";
import { useAppTabs } from "hooks/useAppTabs";
import useAuthorized from "hooks/useAuthorized";
import usePageTitle from "hooks/usePageTitle";
import useShowDisabledTags from "hooks/useShowDisabledTags";
import { SendEmailModal } from "routes/campaign/components/Modals";
import ContactInfoPanel from "routes/contact/components/ContactInfoPanel";
import CampaignTable from "routes/entity/components/campaign_table/CampaignTableV3";
import { useContactNavigation } from "routes/contact/useContactNavigation";
import ActivityService from "services/Activity";
import ContactService from "services/Contact";
import EntityService from "services/Entity";

const CAN_CALL_PERMISSIONS = [
  ACL_RELATIONSHIPS.dialSession.read,
  ACL_RELATIONSHIPS.dialSessionPrivate.create,
  ACL_RELATIONSHIPS.callQueueNextContact.read,
  ACL_RELATIONSHIPS.callQueueAddContact.create,
  ACL_RELATIONSHIPS.disposition.read,
  ACL_RELATIONSHIPS.emailUrlForms.read,
  ACL_RELATIONSHIPS.voicemailForFilters.read,
  ACL_RELATIONSHIPS.twilioToken.read,
  ACL_RELATIONSHIPS.tag.read,
  ACL_RELATIONSHIPS.user.read,
  ACL_RELATIONSHIPS.campaignStatuses.read,
  ACL_RELATIONSHIPS.twilioDisposition.create,
  ACL_RELATIONSHIPS.twilioAddToCall.create,
  ACL_RELATIONSHIPS.twilioRemoveFromCall.create,
  ACL_RELATIONSHIPS.twilioUpdateParticipant.create,
];
const CAN_SEND_EMAIL_PERMISSIONS = [
  ACL_RELATIONSHIPS.emailTemplates.read,
  ACL_RELATIONSHIPS.contactEmail.create,
];
const CAN_EDIT_CONTACT_PERMISSIONS = [
  ACL_RELATIONSHIPS.entityContact.edit,
  ACL_RELATIONSHIPS.entityForFilters.read,
];

const initialContact = {
  entity_id: null,
  full_name: "",
  alternate_names: [],
  email_addresses: [],
  title: "",
  department_name: "",
  phone_numbers: [],
  custom_fields: [],
};

const initialEntity = {
  id: null,
  tags: [noTag],
  name: "",
  url: "",
  account_managers: [],
  custom_fields: [],
};

const fetchContact = async (contactId, showDisabledTags) => {
  const contact = await ContactService.getContact(contactId, {
    show_disabled_tags: showDisabledTags,
  });
  if (contact.tags.length === 0) {
    contact.tags = [noTag];
  }
  return contact;
};

const fetchEntity = async (entityId, showDisabledTags) => {
  let entity = await EntityService.getEntity(entityId, {
    show_disabled_tags: showDisabledTags,
  });
  if (entity.tags.length === 0) {
    entity.tags = [noTag];
  }
  return entity;
};

const Contact = () => {
  const { id: contactId } = useParams();
  const { activeTab, handleTabChange: onTabChange } = useAppTabs({
    basePath: `/contacts/${contactId}`,
    defaultTab: "activityLog",
  });
  // A note's "See Original" link opens the activity log at the referenced entry.
  const [auditLogId, setAuditLogId] = useState(null);
  const [auditLogRefreshFlag, setAuditLogRefreshFlag] = useState(1);
  // Reflects a dial session started in another window while this contact is
  // open, tracked via the storage event; it does not read a stored session at
  // mount, so a stale key left by a closed dialer never disables the button.
  const [activeSession, setActiveSession] = useState(null);
  const showDisabledTags = useShowDisabledTags();
  const showDisabledTagsParam = showDisabledTags ? "true" : "false";

  const canCall = useAuthorized(CAN_CALL_PERMISSIONS);
  const canSendEmail = useAuthorized(CAN_SEND_EMAIL_PERMISSIONS);
  const canEditContact = useAuthorized(CAN_EDIT_CONTACT_PERMISSIONS);
  const { openEdit } = useContactNavigation();

  useEffect(() => {
    const onStorage = () => setActiveSession(getSession());
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // A session already running when this page mounted is invisible to the storage
  // event, so gate the call here too. The stored id carries no liveness signal
  // and a session's server status never flips on close, so a stale key cannot be
  // told from a live one; ask the user rather than block, and clear the key when
  // they proceed. Resolves the click-time open once the user answers.
  const [sessionConfirmOpen, setSessionConfirmOpen] = useState(false);
  const sessionConfirmResolve = useRef(null);

  const confirmNoActiveSession = () => {
    if (!getSession()) return true;
    return new Promise(resolve => {
      sessionConfirmResolve.current = resolve;
      setSessionConfirmOpen(true);
    });
  };

  const closeSessionConfirm = proceed => {
    const resolve = sessionConfirmResolve.current;
    if (!resolve) return;
    sessionConfirmResolve.current = null;
    setSessionConfirmOpen(false);
    if (proceed) removeSession();
    resolve(proceed);
  };

  const { data: contact, isFetched: isContactFetched } = useQuery({
    queryKey: ["contact", contactId, showDisabledTagsParam],
    queryFn: () => fetchContact(contactId, showDisabledTagsParam),
    enabled: !!contactId,
    initialData: initialContact,
  });

  const { data: entity, isFetched: isEntityFetched } = useQuery({
    queryKey: ["entity", contact?.entity_id, showDisabledTagsParam],
    queryFn: () => fetchEntity(contact.entity_id, showDisabledTagsParam),
    enabled: !!contact?.entity_id,
    initialData: initialEntity,
  });

  const { data: hasNotes } = useQuery({
    queryKey: ["contact", contactId, "hasNotes"],
    queryFn: () => ActivityService.hasNotes(contactId, "Contact"),
    enabled: !!contactId,
  });

  const linkedTabs = useLinkedTables("Contact", contactId);

  usePageTitle(contact.full_name || "Pleteo");

  const invalidateContactDetailQueries = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ["contact", contactId] });
    await queryClient.invalidateQueries({
      queryKey: ["entity", contact?.entity_id],
    });
    setAuditLogRefreshFlag(prev => prev + 1);
  }, [contactId, contact]);

  const fetchEmailedForms = useCallback(
    async filters => {
      filters = filters || {};
      return await ContactService.getEmails(contactId, filters);
    },
    [contactId]
  );

  const handleTabChange = useCallback(
    (e, { name, auditLogId = null }) => {
      setAuditLogId(auditLogId);
      onTabChange(undefined, { name });
    },
    [onTabChange]
  );

  // The Activity Log deep-link (set by a note's "See Original") applies once.
  // Clear it when the active section changes away from the Activity Log so a
  // later return shows the newest page, not the old entry. The sidebar rail
  // navigates on its own, so no tab-click handler runs here; the reset is done
  // during render per React's "adjust state when a value changes" guidance.
  const [lastSection, setLastSection] = useState(activeTab);
  if (activeTab !== lastSection) {
    setLastSection(activeTab);
    if (activeTab !== "activityLog") {
      setAuditLogId(null);
    }
  }

  const sections = useMemo(() => {
    const items = [
      {
        key: "activityLog",
        label: "Activity Log",
        icon: ClipboardList,
        content: (
          <AuditLog
            modelId={contactId}
            modelType="Contact"
            auditLogId={auditLogId}
            updateFlag={auditLogRefreshFlag}
          />
        ),
      },
      {
        key: "notes",
        label: "Notes",
        icon: StickyNote,
        badge: hasNotes && activeTab !== "notes" && (
          <span
            aria-label="Has notes"
            className="tw:size-2 tw:rounded-full tw:bg-sidebar-primary"
          />
        ),
        content: (
          <NotesView
            modelType="Contact"
            modelId={+contactId}
            handleTabChange={handleTabChange}
          />
        ),
      },
      {
        key: "responses",
        label: "Responses",
        icon: MessagesSquare,
        content: (
          <ResponsesView
            modelId={contactId}
            modelType="Contact"
            fetchEmailedForms={fetchEmailedForms}
          />
        ),
      },
      {
        key: "calls",
        label: "Calls",
        icon: Phone,
        content: (
          <PageContainer styles={SECTION_TABLE_STYLES}>
            <CallsTable entityId={contact?.entity_id} contactId={+contactId} />
          </PageContainer>
        ),
      },
      {
        key: "campaigns",
        label: "Campaigns",
        icon: Megaphone,
        content: (
          <PageContainer styles={SECTION_TABLE_STYLES}>
            <CampaignTable
              contactId={contactId}
              sendEmail={isContactEmailable(contact)}
            />
          </PageContainer>
        ),
      },
      {
        key: "revenueOpportunities",
        label: "Revenue Opportunities",
        icon: Gem,
        content: (
          <PageContainer styles={SECTION_TABLE_STYLES}>
            <RevenueOpportunityTable
              contactId={contactId}
              entityId={contact?.entity_id}
            />
          </PageContainer>
        ),
      },
    ];
    items.push(...linkedTabSections(linkedTabs, contactId));
    return items;
  }, [
    contactId,
    contact,
    activeTab,
    auditLogId,
    auditLogRefreshFlag,
    handleTabChange,
    hasNotes,
    fetchEmailedForms,
    linkedTabs,
  ]);

  const actions = (
    <>
      {canCall && contact?.id && (
        <>
          <CallModal
            contactId={contact.id}
            disabled={!!activeSession || !isContactCallable(contact)}
            onBeforeOpen={confirmNoActiveSession}
            trigger={
              <Button variant="outline" size="sm">
                <Phone data-icon="inline-start" />
                Call
              </Button>
            }
          />
          <ConfirmDialog
            open={sessionConfirmOpen}
            onOpenChange={open => {
              if (!open) closeSessionConfirm(false);
            }}
            title="Start a new dial session?"
            description="A dial session may already be open in another window. Starting a new call creates a second session."
            confirmText="Start Call"
            onConfirm={() => closeSessionConfirm(true)}
          />
        </>
      )}
      {canSendEmail && contact?.id && (
        <SendEmailModal
          contactId={contact.id}
          contactEmail={contact.email || ""}
          trigger={
            <Button
              variant="outline"
              size="sm"
              disabled={!isContactEmailable(contact)}
            >
              <Mail data-icon="inline-start" />
              Email
            </Button>
          }
        />
      )}
      {canEditContact && contact?.id && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => openEdit(contact.id)}
        >
          <SquarePen data-icon="inline-start" />
          Edit
        </Button>
      )}
    </>
  );

  return (
    <SidebarDetailLayout
      sections={sections}
      basePath={`/contacts/${contactId}`}
      defaultSection="activityLog"
      header="Contact"
      breadcrumb={
        <DetailBreadcrumb
          parentTo="/contacts"
          parentLabel="All Contacts"
          current={contact?.full_name}
        />
      }
      title={contact?.full_name}
      titleActions={actions}
      info={
        <ContactInfoPanel
          contact={contact}
          entity={entity}
          showDisabledTags={showDisabledTags}
          onUpdate={invalidateContactDetailQueries}
          isLoading={!isContactFetched}
          isEntityLoading={!!contact?.entity_id && !isEntityFetched}
        />
      }
      infoLabel="Contact Details"
    />
  );
};

export default Contact;
